import Button from '@liquidweb-dev/undercurrent/Button';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';

import CircleXIcon from '@liquidweb-dev/undercurrent/icons/lucide/CircleX';
import TriangleAlertIcon from '@liquidweb-dev/undercurrent/icons/lucide/TriangleAlert';
import XIcon from '@liquidweb-dev/undercurrent/icons/lucide/X';

import TypographyOverflow from '@liquidweb-dev/undercurrent/TypographyOverflow';
import {
	CRITICAL_SEVERITY,
	ERROR_SEVERITY,
	WARNING_SEVERITY,
} from 'utility/constants/notificationConstants';

/**
 * @typedef SeverityIconProps
 * @property {string} severity
 */

/**
 * @param {SeverityIconProps & import('@liquidweb-dev/undercurrent/types/svgTypes').LucideSvgProps} props
 */
const SeverityIcon = ({ severity, ...rest }) => {
	if ([CRITICAL_SEVERITY, ERROR_SEVERITY].includes(severity)) {
		return (
			<CircleXIcon
				sx={(theme) => ({
					color: theme.vars.palette.uc.danger.icon.main,
					flexShrink: 0,
				})}
				{...rest}
			/>
		);
	}
	if (WARNING_SEVERITY === severity) {
		return (
			<TriangleAlertIcon
				sx={(theme) => ({
					color: theme.vars.palette.uc.warning.icon.main,
					flexShrink: 0,
				})}
				{...rest}
			/>
		);
	}
	return null;
};

const stripHtml = (text) => {
	return text.replace(/<[^>]*>?/g, '');
};

const stripLineBreaks = (text) => {
	return text.replace(/\n/g, '');
};

/**
 * @typedef AlertItemPropsI
 * @property {import('@liquidweb-dev/club-sauce/public/notifications/index.raw').LWApiPublicNotificationDetailsResultRawI} alert
 * @property {() => void} onClick
 * @property {() => void} onDismissClick
 */

/**
 * @param {AlertItemPropsI} props
 */
const AlertItem = ({ alert, onClick, onDismissClick }) => (
	<Button
		sx={{
			justifyContent: 'start',
			fontWeight: 400,
			borderRadius: ({ borderRadius }) => borderRadius.roundedXS,
		}}
		variant="ghost"
		onClick={onClick}
	>
		<Stack
			direction="row"
			sx={{ width: '100%', justifyContent: 'space-between' }}
		>
			<Stack
				direction="row"
				sx={{ alignItems: 'center', gap: 100, minWidth: 0, flex: 1 }}
			>
				<SeverityIcon severity={alert.severity} />
				<TypographyOverflow tooltipProps={{ title: '' }} variant="label-lg">
					{stripLineBreaks(stripHtml(alert?.description))}
				</TypographyOverflow>
			</Stack>
			<Tooltip placement="bottom" title="Dismiss" variant="primary">
				<IconButton
					variant="barebones"
					onClick={(event) => {
						event.stopPropagation();
						onDismissClick();
					}}
				>
					<XIcon />
				</IconButton>
			</Tooltip>
		</Stack>
	</Button>
);

export default AlertItem;
